// Iter61k — MobilePlanningPage — iOS Calendar-style compact list.
// Uses /tasks (not /planning/events) + view selector (Privé / Équipe / Personnalisée).
import { useEffect, useState, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChevronLeft, ChevronRight } from '../components/icons';
import { localToday, parseLocalDate } from '../lib/dates';


export default function MobilePlanningPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthOffset, setMonthOffset] = useState(0);
  const [view, setView] = useState('mine'); // 'mine' | 'team' | 'custom'
  const [customUserId, setCustomUserId] = useState(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const reload = async () => {
    // Iter 62bx — Robust reload : each endpoint has its own try/catch so a
    // single failure doesn't kill the whole calendar view.
    try {
      const t = await api.get('/tasks');
      // Iter 62cz — Isolate admin_todo tasks from mobile planning view
      setTasks((t.data || []).filter((x) => x.source !== 'admin_todo'));
    } catch { setTasks([]); }
    try {
      const u = await api.get('/users');
      setUsers((u.data || []).filter((x) => !x.is_archived));
    } catch { setUsers([]); }
    try {
      const p = await api.get('/projects');
      setProjects(p.data || []);
    } catch { setProjects([]); }
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const taskDateFor = (t, viewerId) => {
    if (t.kind === 'video' && t.assigned_to_derush === viewerId && !t.derush_uploaded) {
      return t.derush_deadline || t.due_date;
    }
    return t.due_date;
  };

  const isInView = (t) => {
    if (t.parent_task_id || t.archived) return false;
    if (view === 'mine') {
      const mine = t.assigned_to === user?.id
        || t.assigned_to_derush === user?.id
        || t.assigned_to_montage === user?.id
        || (t.assignees || []).includes(user?.id);
      if (!mine) return false;
      return Boolean(taskDateFor(t, user?.id));
    }
    if (view === 'custom' && customUserId) {
      const isCust = t.assigned_to === customUserId
        || t.assigned_to_derush === customUserId
        || t.assigned_to_montage === customUserId
        || (t.assignees || []).includes(customUserId);
      if (!isCust) return false;
      return Boolean(taskDateFor(t, customUserId));
    }
    return Boolean(t.due_date);
  };

  const viewerId = view === 'mine' ? user?.id : (view === 'custom' ? customUserId : null);

  const { days, monthLabel } = useMemo(() => buildMonth(monthOffset), [monthOffset]);

  const tasksByDay = useMemo(() => {
    const m = {};
    tasks.forEach((t) => {
      if (!isInView(t)) return;
      const dt = viewerId ? taskDateFor(t, viewerId) : t.due_date;
      if (!dt) return;
      const k = dt.slice(0, 10);
      if (!m[k]) m[k] = [];
      m[k].push(t);
    });
    // sort each day
    Object.keys(m).forEach((k) => m[k].sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return m;
  }, [tasks, view, customUserId, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const todayIso = localToday();
  const [selectedDay, setSelectedDay] = useState(todayIso);
  const daysEventsFor = tasksByDay[selectedDay] || [];

  const projectColor = (pid) => projects.find((p) => p.id === pid)?.color || '#5BBEFC';

  const viewLabel = view === 'mine' ? 'Mon planning' : view === 'team' ? "Vue d'équipe" : 'Vue personnalisée';

  return (
    <div style={{ padding: '16px 12px 90px', minHeight: '100dvh', background: 'var(--bg)' }} data-testid="mobile-planning-page">
      {/* View selector */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[
          { k: 'mine', l: 'Privé' },
          { k: 'team', l: 'Équipe' },
          { k: 'custom', l: 'Personnalisé' },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`mobile-planning-view-${t.k}`}
            onClick={() => {
              setView(t.k);
              if (t.k === 'custom' && !customUserId) setShowCustomPicker(true);
            }}
            style={{
              flex: 1, padding: '9px 6px', borderRadius: 10,
              background: view === t.k ? 'rgba(46,163,242,0.16)' : 'var(--sf2)',
              border: `1px solid ${view === t.k ? 'rgba(46,163,242,0.40)' : 'var(--bd)'}`,
              color: view === t.k ? '#5BBEFC' : 'var(--t2)',
              fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
              cursor: 'pointer',
            }}
          >{t.l}</button>
        ))}
      </div>

      {view === 'custom' && (
        <button
          onClick={() => setShowCustomPicker(true)}
          data-testid="mobile-planning-user-picker"
          style={{
            width: '100%', padding: '10px 12px', borderRadius: 10, marginBottom: 12,
            background: 'var(--sf2)', border: '1px solid var(--bd)',
            fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: 'var(--t1)',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          {customUserId ? `Membre : ${users.find((u) => u.id === customUserId)?.first_name || users.find((u) => u.id === customUserId)?.name || '—'}` : 'Choisir un membre…'}
        </button>
      )}

      {/* Month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 12px' }}>
        <button onClick={() => setMonthOffset(monthOffset - 1)} style={ctrlBtn}><ChevronLeft size={20} color="var(--t2)" /></button>
        <h1 className="asa-display" style={{ fontSize: 22, margin: 0, textTransform: 'capitalize' }}>{monthLabel}</h1>
        <button onClick={() => setMonthOffset(monthOffset + 1)} style={ctrlBtn}><ChevronRight size={20} color="var(--t2)" /></button>
      </div>

      <div style={{ fontFamily: 'Lexend', fontSize: 11, color: 'var(--t3)', marginBottom: 8, textAlign: 'center' }}>
        {viewLabel}
      </div>

      {/* Weekday header */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 6, padding: '0 2px' }}>
        {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontFamily: 'Sora', fontWeight: 700, fontSize: 11, color: 'var(--t3)' }}>{d}</div>
        ))}
      </div>

      {/* Month grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {days.map((d, i) => {
          const iso = d.iso;
          const isToday = iso === todayIso;
          const isSelected = iso === selectedDay;
          const inMonth = d.inMonth;
          const has = (tasksByDay[iso] || []).length > 0;
          return (
            <button
              key={i}
              onClick={() => setSelectedDay(iso)}
              data-testid={`mobile-cal-day-${iso}`}
              style={{
                aspectRatio: '1', borderRadius: 10,
                background: isSelected ? 'rgba(46,163,242,0.20)' : 'transparent',
                border: isSelected ? '1.5px solid #2EA3F2' : '1.5px solid transparent',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                cursor: 'pointer', padding: 2,
              }}
            >
              <div style={{
                fontFamily: 'Sora', fontWeight: isToday ? 800 : 600, fontSize: 13,
                color: !inMonth ? 'var(--t4)' : isToday ? '#FF6B6B' : 'var(--t1)',
              }}>{d.day}</div>
              {has && inMonth && (
                <div style={{ width: 4, height: 4, borderRadius: 999, background: isSelected ? '#2EA3F2' : '#5BBEFC' }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Selected day tasks list */}
      <div style={{ marginTop: 20 }}>
        <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 15, color: 'var(--t1)', marginBottom: 10, textTransform: 'capitalize' }}>
          {formatDayLabel(selectedDay)}
        </div>
        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Chargement…</div>
        ) : daysEventsFor.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--t3)', fontSize: 13, border: '1.5px dashed var(--bd)', borderRadius: 12, fontFamily: 'Lexend' }}>
            Rien de prévu
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {daysEventsFor.map((t) => (
              <div key={t.id} data-testid={`mobile-task-${t.id}`} style={{
                display: 'flex', gap: 12, alignItems: 'stretch',
                padding: '12px 14px', borderRadius: 12,
                background: 'var(--sf)', border: '1px solid var(--bd)',
              }}>
                <div style={{ width: 4, borderRadius: 4, background: projectColor(t.project_id) }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.title || 'Sans titre'}
                  </div>
                  {(t.start_time || t.end_time) && (
                    <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>
                      {t.start_time || ''}{t.end_time ? ` – ${t.end_time}` : ''}
                    </div>
                  )}
                  {t.kind === 'video' && (
                    <div style={{ fontFamily: 'Sora', fontSize: 10.5, fontWeight: 700, color: '#A78BFA', marginTop: 4 }}>VIDEO</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom user picker */}
      {showCustomPicker && (
        <div
          onClick={() => setShowCustomPicker(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,10,18,0.65)', backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'var(--sf)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '18px 18px calc(env(safe-area-inset-bottom, 0px) + 18px)', maxHeight: '70vh', overflowY: 'auto' }}
          >
            <div style={{ width: 40, height: 4, borderRadius: 4, background: 'var(--t4)', margin: '0 auto 14px' }} />
            <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 16, color: 'var(--t1)', marginBottom: 12 }}>Choisir un membre</div>
            {users.map((u) => (
              <button
                key={u.id}
                data-testid={`mobile-planning-user-${u.id}`}
                onClick={() => { setCustomUserId(u.id); setShowCustomPicker(false); }}
                style={{
                  width: '100%', padding: '12px 14px', borderRadius: 10, marginBottom: 6,
                  background: customUserId === u.id ? 'rgba(46,163,242,0.14)' : 'var(--sf2)',
                  border: '1px solid var(--bd)', cursor: 'pointer', textAlign: 'left',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)',
                }}
              >{u.first_name || u.name} <span style={{ color: 'var(--t3)', fontWeight: 400 }}>· {u.role}</span></button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


const ctrlBtn = {
  width: 40, height: 40, borderRadius: 10,
  background: 'var(--sf2)', border: '1px solid var(--bd)',
  display: 'grid', placeItems: 'center', cursor: 'pointer',
};


function buildMonth(offset) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const year = base.getFullYear();
  const month = base.getMonth();
  const firstDay = new Date(year, month, 1);
  let firstDow = firstDay.getDay();
  firstDow = (firstDow + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const prevMonth = month - 1 < 0 ? 11 : month - 1;
    const prevYear = month - 1 < 0 ? year - 1 : year;
    days.push({ day: d, inMonth: false, iso: fmtIso(prevYear, prevMonth, d) });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, inMonth: true, iso: fmtIso(year, month, d) });
  }
  while (days.length % 7 !== 0) {
    const extra = days.length - (firstDow + daysInMonth) + 1;
    const nextMonth = month + 1 > 11 ? 0 : month + 1;
    const nextYear = month + 1 > 11 ? year + 1 : year;
    days.push({ day: extra, inMonth: false, iso: fmtIso(nextYear, nextMonth, extra) });
  }
  const monthLabel = base.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return { days, monthLabel };
}
function fmtIso(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function formatDayLabel(iso) {
  try {
    const d = parseLocalDate(iso);
    if (!d) return iso;
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch { return iso; }
}
